import { useField } from 'formik';
import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';

import { usePrevious } from '@hivebrite/react-hooks';

import { useGlobalStorageFiles } from 'hb-react/shared/hooks/useGlobalStorageV2';
import {
  type GlobalStorageFile,
  type StorageAttachmentRecord,
} from 'hb-react/shared/hooks/useGlobalStorageV2/types';
import {
  getInitialFiles,
  formatRemovedFilesForSubmit,
  getStorageAttributesFieldName,
  formatAddedFilesForSubmit,
} from 'hb-react/shared/hooks/useGlobalStorageV2/utils';

/**
 * This hook lets you easily manage multiple Global Storage files in a formik form. It manages the GS files internally and exposes two formik fields:
 *
 * **\<fieldName\>:** Array of NEW files that have not been persisted to a record yet.
 * **\<fieldName>_attachments_attributes\>:** Array of EXISTING files that the user wants to DELETE.
 *
 * ### Testing
 *
 * This hook relies on both Formik and Global Storage which makes it a bit complex to test. Here's a simple example of jumping through all the hoops:
 *
 *```ts
 *  import {
 *    getPresignHandler,
 *    postUploadHandler,
 *  } from 'hb-react/shared/hooks/useGlobalStorageV2/mocks/api';
 *  import * as globalStorageUtils from 'hb-react/shared/hooks/useGlobalStorageV2/utils';
 *
 *  ...
 *
 *  // Spy on the getInitialFiles function
 *  const getInitialFilesSpy = jest.spyOn(globalStorageUtils, 'getInitialFiles');
 *
 *  ...
 *
 *  const waitForUseUploadFieldInitialization = async () => {
 *    // Wait for useUploadField to finish initializing the values
 *    await waitFor(() => {
 *      expect(getInitialFilesSpy).toHaveBeenCalled();
 *    });
 *  };
 *
 *  ...
 *
 *  describe('YourComponentNameHere', () => {
 *    beforeEach(() => {
 *      server.use(getPresignHandler(), postUploadHandler());
 *      jest.clearAllMocks();
 *    });
 *
 *    it('renders', async () => {
 *      renderComponent();
 *
 *      await waitForUseUploadFieldInitialization();
 *
 *      // Your assertions go here
 *      ...
 *    });
 *
 *    ...
 *
 *  });
 *
 * ```
 */
export const useUploadField = ({
  fieldName,
  deletedIdsFieldName,
  filesValidator,
  initialContentHtml,
  markFileAsRemovedOnError,
  maxFileSize,
  initialValues,
}: {
  /** The name of the fields holding the deleted uploads ids */
  deletedIdsFieldName?: string;
  /** The name of the field. */
  fieldName: string;
  /** Formik field-level validation */
  filesValidator: (value?: Array<GlobalStorageFile>) => string | undefined;
  /** The initial content HTML to be used to initialize the field. If your field is not a WYSIWYG editor, you can ignore this. */
  initialContentHtml?: string;
  /** The initial files to be used to get the initial value of the field. Can be used to avoid formik dirty state on load */
  initialValues?: Array<StorageAttachmentRecord>;
  /** Wether we should automatically mark a file as removed if an error is detected. Normally, files with an error are not automatically "removed" so the user can see the error and manually remove the file after. */
  markFileAsRemovedOnError?: boolean;
  /** In bytes to match File.size */
  maxFileSize?: number;
}) => {
  const useIdsForDeletion = !!deletedIdsFieldName;

  const attributesFieldName = getStorageAttributesFieldName(fieldName);
  const deleteStorageRecordFieldName = useIdsForDeletion
    ? deletedIdsFieldName
    : attributesFieldName;

  const [field, fieldMeta, fieldHelper] = useField({
    name: fieldName,
    validate: (value) => value?.error,
  });

  const [, , deleteStorageRecordFieldHelper] = useField({
    name: deleteStorageRecordFieldName,
  });

  const initialFiles = useMemo(
    () =>
      getInitialFiles({
        files: initialValues || fieldMeta.initialValue,
        contentHtml: initialContentHtml,
      }),
    [fieldMeta.initialValue, initialContentHtml, initialValues],
  );

  const { files, setFiles, addFiles, removeFiles } = useGlobalStorageFiles({
    initialFiles,
    config: {
      maxFileSize,
      markFileAsRemovedOnError,
    },
  });
  const prevFiles = usePrevious(files);

  useEffect(() => {
    const filesEmpty = !files || files.length === 0;
    const prevFilesEmpty = !prevFiles || prevFiles.length === 0;

    if (filesEmpty && prevFilesEmpty) {
      return;
    }

    if (!isEqual(files, prevFiles)) {
      const error = filesValidator(files);

      const newValue = error ? { error } : formatAddedFilesForSubmit(files);

      // Added files
      fieldHelper.setValue(newValue, true);
      fieldHelper.setTouched(true, false);

      // Removed files
      const removedFilesAttributes = formatRemovedFilesForSubmit(files);

      if (useIdsForDeletion) {
        deleteStorageRecordFieldHelper.setValue(
          removedFilesAttributes.map(({ id }) => id),
          false,
        );
        deleteStorageRecordFieldHelper.setTouched(true, false);
      } else {
        deleteStorageRecordFieldHelper.setValue(removedFilesAttributes, false);
        deleteStorageRecordFieldHelper.setTouched(true, false);
      }
    }
  }, [
    prevFiles,
    files,
    fieldHelper,
    filesValidator,
    deleteStorageRecordFieldHelper,
    useIdsForDeletion,
    initialValues, // Necessary to support enableReinitialize in Formik
  ]);

  return {
    files,
    field,
    fieldMeta,
    setFiles,
    addFiles,
    removeFiles,
  };
};
